import React, { useMemo } from "react";
import { Truck, Banknote, Clock, Weight } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "./Dashboard.scss";
import "./LogisticsDemoPanelMockup.scss";

const TIPO_CARGA = [
  { name: "Completa", value: 52, color: "#2563eb" },
  { name: "Parcial", value: 28, color: "#7c3aed" },
  { name: "Consolidada", value: 20, color: "#059669" },
];

const RUTAS_TONELADAS = [
  { ruta: "BOG-MED", ton: 420 },
  { ruta: "BOG-CLO", ton: 310 },
  { ruta: "MED-BAQ", ton: 280 },
  { ruta: "CLO-PER", ton: 195 },
  { ruta: "BOG-BAQ", ton: 175 },
];

const CLIENTES_DEMO = [
  "Transportes del Norte",
  "Distribuciones Omega",
  "Comercializadora Beta",
  "Importadora Caribe",
  "Suministros Pacífico",
  "Grupo Logístico 360",
  "Alimentos Río",
];

function buildRandomDemoData() {
  const rnd = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

  const viajesSemanales = ["S1", "S2", "S3", "S4", "S5", "S6", "S7", "S8"].map(
    (semana) => ({
      semana,
      viajes: rnd(17, 53),
    })
  );

  const facturacionMensual = ["Sep", "Oct", "Nov", "Dic", "Ene", "Feb"].map(
    (mes) => ({
      mes,
      millonesCOP: rnd(1620, 3180),
    })
  );

  const viajesPorCliente = CLIENTES_DEMO.map((cliente) => ({
    cliente,
    viajes: rnd(9, 47),
  })).sort((a, b) => b.viajes - a.viajes);

  const viajesUltimas4Semanas = viajesSemanales
    .slice(-4)
    .reduce((sum, row) => sum + row.viajes, 0);
  const ultimoMesFacturacion =
    facturacionMensual[facturacionMensual.length - 1].millonesCOP;

  return {
    viajesSemanales,
    facturacionMensual,
    viajesPorCliente,
    viajesUltimas4Semanas,
    ultimoMesFacturacion,
  };
}

/**
 * Mock logistics dashboard for LOGISTICS_DEMO — shown inside Panel de control.
 * Series vary on each visit (randomized locally, no backend).
 */
function LogisticsDemoPanelMockup() {
  const demo = useMemo(() => buildRandomDemoData(), []);

  const fmtMillones = (n) =>
    `$${n.toLocaleString("es-CO", { maximumFractionDigits: 0 })}M`;

  return (
    <div className="logistics-demo-panel roldan-dashboard-view">
      <div className="dashboard-header logistics-demo-section-header">
        <h2 className="logistics-demo-section-title">Indicadores logísticos</h2>
        <span className="logistics-demo-period">Últimos 30 días</span>
      </div>

      <div className="kpi-cards">
        <div className="kpi-card">
          <Truck size={32} />
          <div>
            <h3>Viajes completados</h3>
            <p className="kpi-value">{demo.viajesUltimas4Semanas}</p>
            <p className="kpi-hint">Suma últimas 4 semanas (referencia)</p>
          </div>
        </div>
        <div className="kpi-card">
          <Banknote size={32} />
          <div>
            <h3>Facturación estimada</h3>
            <p className="kpi-value">{fmtMillones(demo.ultimoMesFacturacion)}</p>
            <p className="kpi-hint">Último mes (millones COP)</p>
          </div>
        </div>
        <div className="kpi-card">
          <Clock size={32} />
          <div>
            <h3>Entregas a tiempo</h3>
            <p className="kpi-value">94%</p>
          </div>
        </div>
        <div className="kpi-card">
          <Weight size={32} />
          <div>
            <h3>Carga movilizada</h3>
            <p className="kpi-value">1.280 t</p>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h2>Viajes por semana</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={demo.viajesSemanales}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="semana" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="linear"
                dataKey="viajes"
                name="Viajes"
                stroke="#2563eb"
                strokeWidth={2}
                dot={{ r: 4 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Facturación mensual (millones COP)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={demo.facturacionMensual}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="millonesCOP"
                name="Millones COP"
                fill="#7c3aed"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card chart-card--viajes-cliente">
          <h2>Distribución de viajes por cliente</h2>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={demo.viajesPorCliente}
              layout="vertical"
              margin={{ left: 8, right: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis
                dataKey="cliente"
                type="category"
                width={148}
                tick={{ fontSize: 11 }}
              />
              <Tooltip />
              <Bar
                dataKey="viajes"
                name="Viajes"
                fill="#4f46e5"
                radius={[0, 6, 6, 0]}
                maxBarSize={28}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Mix de tipo de carga</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={TIPO_CARGA}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, value }) => `${name} ${value}%`}
              >
                {TIPO_CARGA.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Principal rutas por tonelaje</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={RUTAS_TONELADAS} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="ruta" type="category" width={88} />
              <Tooltip />
              <Bar dataKey="ton" name="Toneladas" fill="#059669" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default LogisticsDemoPanelMockup;
